import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["viewBox"]
const _hoisted_2 = /*#__PURE__*/_createElementVNode("defs", null, [
  /*#__PURE__*/_createElementVNode("linearGradient", {
    id: "taggy-promo-outline-gradient",
    x1: "0%",
    y1: "0%",
    x2: "100%",
    y2: "0%"
  }, [
    /*#__PURE__*/_createElementVNode("stop", {
      offset: "0%",
      "stop-color": "#5022a6"
    }),
    /*#__PURE__*/_createElementVNode("stop", {
      offset: "100%",
      "stop-color": "#e5a1ac"
    })
  ])
], -1)
const _hoisted_3 = ["x", "y", "width", "height", "rx", "ry"]
const _hoisted_4 = ["x", "y", "width", "height", "rx", "ry"]
const _hoisted_5 = { class: "taggy-promo__banner-layout" }
const _hoisted_6 = { class: "taggy-promo__banner-copy" }
const _hoisted_7 = { class: "taggy-promo__brand" }
const _hoisted_8 = ["src", "alt"]
const _hoisted_9 = { class: "taggy-promo__heading" }
const _hoisted_10 = { class: "taggy-promo__title-row" }
const _hoisted_11 = { class: "taggy-promo__title" }
const _hoisted_12 = { class: "assistant-beta-badge" }
const _hoisted_13 = { class: "taggy-promo__eyebrow" }
const _hoisted_14 = { class: "taggy-promo__body" }
const _hoisted_15 = { class: "taggy-promo__footer" }
const _hoisted_16 = { class: "taggy-promo__dismiss" }
const _hoisted_17 = ["checked"]

import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import i18n from "@/core/i18n/i18n";
import taggyLogo from "@/core/layout/assets/small-logo.png";

type ResizeObserverLike = {
  disconnect: () => void;
  observe: (target: Element) => void;
};

type ResizeObserverConstructorLike = new (
  callback: (...args: any[]) => void
) => ResizeObserverLike;
type BannerLayoutMode = "default" | "centered" | "stacked";

const TRIGGER_ID = "taggy-wizard-trigger";
const CANVAS_SELECTOR = "[data-taggy-flow-canvas]";
const SHOW_DELAY_MS = 800;
const DISMISS_MS = 8000;
const COUNTDOWN_STEPS = 100;
const VIEWPORT_MARGIN = 16;
const BANNER_SIDE_INSET = 24;
const BANNER_BOTTOM_INSET = 24;
const CENTERED_ACTIONS_BREAKPOINT = 800;
const STACKED_ACTIONS_BREAKPOINT = 460;
const MIN_RENDER_WIDTH_BREAKPOINT = 260;
const OUTLINE_STROKE_WIDTH = 2;
const OUTLINE_RADIUS = 12;


export default /*#__PURE__*/_defineComponent({
  __name: 'TaggyPromo',
  props: {
    promoHidden: { type: Boolean },
    screenKey: {},
    screenVisible: { type: Boolean },
    wizardOpen: { type: Boolean }
  },
  emits: ["hide-promo", "open-wizard"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const { t } = i18n.global;
const visible = ref(false);
const progressRatio = ref(1);
const dismissChecked = ref(false);
const dismissedForScreen = ref(false);
const promoRef = ref<HTMLElement | null>(null);
const targetRect = ref<DOMRect | null>(null);
const outlineBox = ref({ width: 0, height: 0 });
const sessionStarted = ref(false);
const showWindowActive = ref(false);
const sessionConsumed = ref(false);

let progressTimer: ReturnType<typeof window.setInterval> | null = null;
let dismissTimer: ReturnType<typeof window.setTimeout> | null = null;
let mountTimer: ReturnType<typeof window.setTimeout> | null = null;
let canvasResizeObserver: ResizeObserverLike | null = null;
let promoResizeObserver: ResizeObserverLike | null = null;
let observedCanvas: HTMLElement | null = null;
let showWindowAt = 0;
let hideWindowAt = 0;

const assistantName = computed(() => t("TeggyAssistant.Name"));
const betaLabel = computed(() => t("TeggyAssistant.Badge"));
const assistantSubtitle = computed(() => t("TeggyAssistant.Eyebrow"));
const assistantDescription = computed(() => t("TeggyAssistant.Description"));
const dismissLabel = computed(() => t("TeggyAssistant.Dismiss"));
const ctaLabel = computed(() => t("TeggyAssistant.Open"));
const outlineStrokeInset = computed(() => OUTLINE_STROKE_WIDTH / 2);
const outlineWidth = computed(() =>
  Math.max(outlineBox.value.width - OUTLINE_STROKE_WIDTH, 0)
);
const outlineHeight = computed(() =>
  Math.max(outlineBox.value.height - OUTLINE_STROKE_WIDTH, 0)
);
const outlineRadius = computed(() =>
  Math.max(
    Math.min(
      OUTLINE_RADIUS - OUTLINE_STROKE_WIDTH / 2,
      outlineWidth.value / 2,
      outlineHeight.value / 2
    ),
    0
  )
);
const outlinePerimeter = computed(() => {
  const width = outlineWidth.value;
  const height = outlineHeight.value;
  const radius = outlineRadius.value;

  if (width <= 0 || height <= 0) {
    return 0;
  }

  return 2 * (width + height - 4 * radius) + 2 * Math.PI * radius;
});
const outlineProgressStyle = computed(() => {
  if (outlinePerimeter.value <= 0) {
    return {};
  }

  return {
    strokeDasharray: `${outlinePerimeter.value * progressRatio.value} ${
      outlinePerimeter.value
    }`,
  };
});

const layoutStyle = computed(() => {
  const rect = targetRect.value;

  if (!rect || rect.width <= 0 || rect.height <= 0) {
    return {
      left: "24px",
      right: "24px",
      bottom: "24px",
      width: "auto",
    };
  }

  const left = Math.max(VIEWPORT_MARGIN, rect.left + BANNER_SIDE_INSET);
  const maxWidth = window.innerWidth - left - VIEWPORT_MARGIN;
  const width = Math.max(
    Math.min(rect.width - BANNER_SIDE_INSET * 2, maxWidth),
    0
  );
  const bottom = Math.max(
    window.innerHeight - rect.bottom + BANNER_BOTTOM_INSET,
    VIEWPORT_MARGIN
  );

  return {
    left: `${left}px`,
    bottom: `${bottom}px`,
    width: `${width}px`,
  };
});

const promoStyle = computed(() => layoutStyle.value);
const promoWidthPx = computed(() => {
  const { width } = layoutStyle.value;

  if (typeof width !== "string" || width === "auto") {
    return null;
  }

  const parsed = Number.parseFloat(width);
  return Number.isFinite(parsed) ? parsed : null;
});
const isTooNarrowToRender = computed(() => {
  if (promoWidthPx.value === null) {
    return false;
  }

  return promoWidthPx.value < MIN_RENDER_WIDTH_BREAKPOINT;
});
const bannerLayoutMode = computed<BannerLayoutMode>(() => {
  if (promoWidthPx.value === null) {
    return "default";
  }

  if (promoWidthPx.value < STACKED_ACTIONS_BREAKPOINT) {
    return "stacked";
  }

  if (promoWidthPx.value < CENTERED_ACTIONS_BREAKPOINT) {
    return "centered";
  }

  return "default";
});

function clearVisibleTimers() {
  if (progressTimer) {
    window.clearInterval(progressTimer);
    progressTimer = null;
  }
}

function clearScheduleTimers() {
  if (dismissTimer) {
    window.clearTimeout(dismissTimer);
    dismissTimer = null;
  }

  if (mountTimer) {
    window.clearTimeout(mountTimer);
    mountTimer = null;
  }
}

function clearAllTimers() {
  clearVisibleTimers();
  clearScheduleTimers();
}

function resolveCanvasAnchor() {
  const candidates = Array.from(
    document.querySelectorAll<HTMLElement>(CANVAS_SELECTOR)
  );

  return (
    candidates.find((candidate) => {
      const rect = candidate.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    }) ||
    candidates[0] ||
    null
  );
}

function updateAnchorPosition() {
  const canvas = resolveCanvasAnchor();
  targetRect.value = canvas?.getBoundingClientRect() ?? null;
}

function updateOutlineGeometry() {
  const promo = promoRef.value;
  if (!promo) {
    outlineBox.value = { width: 0, height: 0 };
    return;
  }

  const rect = promo.getBoundingClientRect();
  outlineBox.value = {
    width: rect.width,
    height: rect.height,
  };
}

function observePromoSize() {
  updateOutlineGeometry();

  if (!promoResizeObserver || !promoRef.value) {
    return;
  }

  promoResizeObserver.disconnect();
  promoResizeObserver.observe(promoRef.value);
}

function stopObservingPromoSize() {
  if (promoResizeObserver) {
    promoResizeObserver.disconnect();
  }
}

function observeCanvasAnchor() {
  const canvas = resolveCanvasAnchor();

  if (!canvasResizeObserver || !canvas) {
    observedCanvas = canvas;
    return;
  }

  if (observedCanvas === canvas) {
    return;
  }

  canvasResizeObserver.disconnect();
  canvasResizeObserver.observe(canvas);
  observedCanvas = canvas;
}

function stopObservingCanvasAnchor() {
  if (canvasResizeObserver) {
    canvasResizeObserver.disconnect();
  }

  observedCanvas = null;
}

function setGlow(active: boolean) {
  const trigger = document.getElementById(TRIGGER_ID);
  if (!trigger) {
    return;
  }

  trigger.classList.toggle("taggy-glow-active", active);
}

function updateProgressRatio() {
  if (!showWindowActive.value) {
    progressRatio.value = 1;
    return;
  }

  const remainingMs = Math.max(hideWindowAt - Date.now(), 0);
  progressRatio.value = Math.min(Math.max(remainingMs / DISMISS_MS, 0), 1);
}

function startCountdown() {
  if (progressTimer) {
    window.clearInterval(progressTimer);
    progressTimer = null;
  }

  updateProgressRatio();
  const intervalMs = DISMISS_MS / COUNTDOWN_STEPS;

  progressTimer = window.setInterval(() => {
    updateProgressRatio();

    if (progressRatio.value <= 0 && progressTimer) {
      window.clearInterval(progressTimer);
      progressTimer = null;
    }
  }, intervalMs);
}

function hideBanner() {
  clearVisibleTimers();
  visible.value = false;
  stopObservingCanvasAnchor();
  stopObservingPromoSize();
  setGlow(false);
}

function resetPresentationState() {
  clearAllTimers();
  sessionStarted.value = false;
  showWindowActive.value = false;
  progressRatio.value = 1;
  hideBanner();
}

function canPresentBanner() {
  return (
    !props.promoHidden &&
    !dismissedForScreen.value &&
    !sessionConsumed.value &&
    showWindowActive.value &&
    props.screenVisible &&
    !props.wizardOpen &&
    !isTooNarrowToRender.value
  );
}

function maybeStartSession() {
  if (
    sessionStarted.value ||
    sessionConsumed.value ||
    props.promoHidden ||
    dismissedForScreen.value ||
    props.wizardOpen ||
    !props.screenVisible
  ) {
    return;
  }

  sessionStarted.value = true;
  showWindowAt = Date.now() + SHOW_DELAY_MS;
  hideWindowAt = showWindowAt + DISMISS_MS;

  mountTimer = window.setTimeout(beginShowWindow, SHOW_DELAY_MS);
  dismissTimer = window.setTimeout(expireSession, SHOW_DELAY_MS + DISMISS_MS);
}

function consumeSession() {
  sessionStarted.value = false;
  sessionConsumed.value = true;
  showWindowActive.value = false;
  clearAllTimers();
  hideBanner();
  progressRatio.value = 1;
}

function expireSession() {
  consumeSession();
}

async function showBanner() {
  if (!canPresentBanner()) {
    hideBanner();
    return;
  }

  updateProgressRatio();
  await nextTick();

  if (!canPresentBanner()) {
    hideBanner();
    return;
  }

  updateAnchorPosition();
  if (!visible.value) {
    visible.value = true;
    await nextTick();
  }

  if (!canPresentBanner()) {
    hideBanner();
    return;
  }

  observePromoSize();
  observeCanvasAnchor();
  setGlow(true);
  startCountdown();
}

function syncPresentation() {
  if (props.wizardOpen) {
    consumeSession();
    return;
  }

  maybeStartSession();

  if (!canPresentBanner()) {
    hideBanner();
    return;
  }

  showBanner();
}

function beginShowWindow() {
  if (sessionConsumed.value) {
    return;
  }

  showWindowActive.value = true;
  updateProgressRatio();
  syncPresentation();
}

function startSession() {
  dismissedForScreen.value = false;
  dismissChecked.value = false;
  sessionConsumed.value = false;
  resetPresentationState();
  syncPresentation();
}

function handleOpen() {
  consumeSession();
  emit("open-wizard");
}

function handleDismissPreferenceChange(event: Event) {
  const target = event.target as HTMLInputElement | null;
  dismissChecked.value = Boolean(target?.checked);

  if (!dismissChecked.value) {
    return;
  }

  dismissedForScreen.value = true;
  resetPresentationState();
  emit("hide-promo");
}

function getResizeObserverConstructor(): ResizeObserverConstructorLike | null {
  if (typeof window === "undefined") {
    return null;
  }

  return (
    (
      window as Window & {
        ResizeObserver?: ResizeObserverConstructorLike;
      }
    ).ResizeObserver || null
  );
}

function handleViewportChange() {
  if (!visible.value) {
    return;
  }

  updateAnchorPosition();
  updateOutlineGeometry();
}

watch(
  () => props.wizardOpen,
  () => {
    syncPresentation();
  }
);

watch(
  () => props.screenKey,
  () => {
    startSession();
  }
);

watch(
  () => props.screenVisible,
  () => {
    syncPresentation();
  }
);

watch(
  () => props.promoHidden,
  (isHidden) => {
    if (isHidden) {
      dismissChecked.value = true;
      resetPresentationState();
      return;
    }

    dismissChecked.value = false;
    if (!dismissedForScreen.value) {
      syncPresentation();
    }
  }
);

watch(isTooNarrowToRender, () => {
  syncPresentation();
});

onMounted(() => {
  const ResizeObserverConstructor = getResizeObserverConstructor();

  if (ResizeObserverConstructor) {
    canvasResizeObserver = new ResizeObserverConstructor(() => {
      updateAnchorPosition();
    });

    promoResizeObserver = new ResizeObserverConstructor(() => {
      updateOutlineGeometry();
    });
  }

  updateAnchorPosition();
  window.addEventListener("resize", handleViewportChange);
  window.addEventListener("scroll", handleViewportChange, true);
  startSession();
});

onUnmounted(() => {
  clearAllTimers();
  stopObservingCanvasAnchor();
  stopObservingPromoSize();
  setGlow(false);
  window.removeEventListener("resize", handleViewportChange);
  window.removeEventListener("scroll", handleViewportChange, true);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    _createVNode(_Transition, { name: "taggy-slide-up" }, {
      default: _withCtx(() => [
        (visible.value)
          ? (_openBlock(), _createElementBlock("section", {
              key: 0,
              ref_key: "promoRef",
              ref: promoRef,
              class: _normalizeClass(['taggy-promo', `taggy-promo--${bannerLayoutMode.value}`]),
              style: _normalizeStyle(promoStyle.value),
              "aria-live": "polite"
            }, [
              (outlineWidth.value > 0 && outlineHeight.value > 0)
                ? (_openBlock(), _createElementBlock("svg", {
                    key: 0,
                    class: "taggy-promo__outline",
                    viewBox: `0 0 ${outlineBox.value.width} ${outlineBox.value.height}`,
                    preserveAspectRatio: "none",
                    "aria-hidden": "true"
                  }, [
                    _hoisted_2,
                    _createElementVNode("rect", {
                      class: "taggy-promo__outline-track",
                      x: outlineStrokeInset.value,
                      y: outlineStrokeInset.value,
                      width: outlineWidth.value,
                      height: outlineHeight.value,
                      rx: outlineRadius.value,
                      ry: outlineRadius.value
                    }, null, 8, _hoisted_3),
                    _createElementVNode("rect", {
                      class: "taggy-promo__outline-progress",
                      x: outlineStrokeInset.value,
                      y: outlineStrokeInset.value,
                      width: outlineWidth.value,
                      height: outlineHeight.value,
                      rx: outlineRadius.value,
                      ry: outlineRadius.value,
                      style: _normalizeStyle(outlineProgressStyle.value)
                    }, null, 12, _hoisted_4)
                  ], 8, _hoisted_1))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("div", _hoisted_6, [
                  _createElementVNode("div", _hoisted_7, [
                    _createElementVNode("img", {
                      class: "taggy-promo__logo",
                      src: _unref(taggyLogo),
                      alt: assistantName.value
                    }, null, 8, _hoisted_8),
                    _createElementVNode("div", _hoisted_9, [
                      _createElementVNode("div", _hoisted_10, [
                        _createElementVNode("strong", _hoisted_11, _toDisplayString(assistantName.value), 1),
                        _createElementVNode("span", _hoisted_12, _toDisplayString(betaLabel.value), 1)
                      ]),
                      _createElementVNode("span", _hoisted_13, _toDisplayString(assistantSubtitle.value), 1)
                    ])
                  ]),
                  _createElementVNode("p", _hoisted_14, _toDisplayString(assistantDescription.value), 1)
                ]),
                _createElementVNode("div", _hoisted_15, [
                  _createElementVNode("label", _hoisted_16, [
                    _createElementVNode("input", {
                      class: "taggy-promo__dismiss-input",
                      type: "checkbox",
                      checked: dismissChecked.value,
                      onChange: handleDismissPreferenceChange
                    }, null, 40, _hoisted_17),
                    _createElementVNode("span", null, _toDisplayString(dismissLabel.value), 1)
                  ]),
                  _createElementVNode("button", {
                    class: "btn btn-oneTeg2 taggy-promo__cta",
                    type: "button",
                    onClick: handleOpen
                  }, _toDisplayString(ctaLabel.value), 1)
                ])
              ])
            ], 6))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ]))
}
}

})